import { useEffect, useRef, useState } from "react"
import { PencilSimple } from "@phosphor-icons/react"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { MedicationDetail } from "@/modules/patients/detail/medication/components/ch-medication-search"
import { generateMedicationDescription } from "@/modules/patients/detail/medication/lib/generate-medication-description"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { handleMedicationSearchItem } from "@/modules/patients/detail/medication/services/useChMedicationSearchItemQuery"
import { Language } from "@/types/__generated__/union-types"

interface MedicationItemProps {
  isActive?: boolean
  medicationData: chMedicationFragmentInline$data
  patientLanguage: Language
  handleSelectExistingMedication: (item: chMedicationFragmentInline$data, medication?: MedicationDetail) => void
  ref?: React.Ref<HTMLButtonElement>
}

export const MedicationItem = (props: MedicationItemProps) => {
  const { isActive = false, medicationData, patientLanguage, handleSelectExistingMedication, ref } = props
  const { id, name, type, pharmaCode, templateId } = medicationData

  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const medicationDescription = generateMedicationDescription({ isActive, ...medicationData })

  const handleSelect = async () => {
    if (type === "PREPARATION" || type === "TEMPLATE") {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setIsLoading(true)

      const medicationDetail = await handleMedicationSearchItem(
        abortControllerRef.current.signal,
        type,
        patientLanguage,
        templateId,
        pharmaCode,
      )

      handleSelectExistingMedication(
        medicationData,
        medicationDetail
          ? {
              ...medicationDetail,
              itemId: type === "TEMPLATE" ? (templateId ?? undefined) : (pharmaCode ?? undefined),
              pharmacode: pharmaCode,
              medicationType: type,
              name,
            }
          : undefined,
      )

      setIsLoading(false)
    } else {
      handleSelectExistingMedication(medicationData)
    }
  }

  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  return (
    <button
      data-testid={normalizeTestId("medication-item-", id)}
      ref={ref}
      className="group hover:bg-surface-hovered active:bg-surface-pressed relative flex min-h-12 w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 text-left transition-all duration-300"
      disabled={isLoading}
      onClick={handleSelect}>
      <div className="overflow-hidden">
        <PotentiallyUnsafeInnerHtml
          className={cn(
            "text-on-surface truncate text-sm leading-5 whitespace-nowrap",
            isFreeTextMedicationType(type) && "pr-1 italic",
          )}
          title={name}
          value={name}
        />
        <p className="text-on-surface-subdued truncate text-xs leading-4 tracking-[0.25px]">{medicationDescription}</p>
      </div>
      <span className="text-on-surface-subdued hover:bg-surface-subdued-hovered ml-auto hidden h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-300 group-hover:flex">
        <PencilSimple />
      </span>
    </button>
  )
}
